import {
  IDownloadReportsBackendService,
  DownloadReportsBackendDependencies,
  Report,
  FilterExpression,
  TransformedReportGroup,
  RawReportType,
  ReportFormatType,
  RawRecordStatus,
  WebSocketMessage,
  ReportMetadata,
  BackendApiResponse,
} from "./downloadReportsBackendService.types";

export class DownloadReportsBackendService implements IDownloadReportsBackendService {
  private readonly eventService: DownloadReportsBackendDependencies["eventService"];
  private readonly backendApi: DownloadReportsBackendDependencies["backendApi"];
  private readonly apiPath: DownloadReportsBackendDependencies["apiPath"];
  private readonly jwtHighbondClient: DownloadReportsBackendDependencies["jwtHighbondClient"];

  private activeSocket: WebSocket | null = null;
  private cancellationUnsubscribe: (() => void) | null = null;

  constructor(dependencies: DownloadReportsBackendDependencies) {
    this.eventService = dependencies.eventService;
    this.backendApi = dependencies.backendApi;
    this.apiPath = dependencies.apiPath;
    this.jwtHighbondClient = dependencies.jwtHighbondClient;
  }

  async getReportTypes(orgId: string | number): Promise<TransformedReportGroup[]> {
    try {
      const url = this.apiPath.reports.getReportTypes(orgId);
      const response: BackendApiResponse<RawReportType[]> = await this.backendApi.publicApiGet(url);

      if (response.error) {
        return [];
      }

      const { data } = response.data;
      return this.transformData(data);
    } catch (error) {
      console.error("Error fetching report types:", error);
      return [];
    }
  }

  emailReport(
    orgId: string | number,
    report: Report,
    filterExpression: FilterExpression
  ): Promise<any> {
    const { reportId, reportName } = report;
    const url = this.apiPath.reports.generateReportUrl(orgId, reportId);

    const body = {
      dataArgs: {
        ...filterExpression,
      },
      reportMetadata: this.createReportMetadata(reportName),
    };

    const options = {};
    return this.generateReport(url, { options, body });
  }

  async downloadReport(
    orgId: string | number,
    report: Report,
    filterExpression: FilterExpression
  ): Promise<void> {
    const { reportId, reportName } = report;
    const reportDispatcherUrl = this.backendApi.getReportDispatcherUrl();

    try {
      this.cleanup();
      const jwtClient = new this.jwtHighbondClient({
        autoRedirect: true,
      });

      const socket = await jwtClient.webSocketHandshakeWithTokenRefresh(reportDispatcherUrl);
      this.activeSocket = socket;
      socket.send(JSON.stringify({ action: "CONNECTION_ID" }));

      this.setupCancellationHandler(socket);

      socket.onmessage = async (response: MessageEvent) => {
        const message: WebSocketMessage = JSON.parse(response.data);
        await this.handleWebSocketMessage(message, orgId, reportId, reportName, filterExpression, socket);
      };

      socket.onclose = () => {
        this.cleanup();
      };

      socket.onerror = (error: Event) => {
        this.eventService.publish("webSocketServerError", error);
        this.cleanup();
      };
    } catch (error) {
      this.eventService.publish("webSocketConnectionError", error);
      this.cleanup();
    }
  }

  async getOpenStatuses(orgId: string | number): Promise<string[]> {
    try {
      const url = this.apiPath.reports.getRecordStatuses(orgId);
      const response: BackendApiResponse<RawRecordStatus[]> = await this.backendApi.publicApiGet(url);

      if (response.error) {
        return [];
      }

      const { data } = response.data;
      const activeStatuses = data.filter((element) => element.attributes.active);

      return activeStatuses.map((status) => status.attributes.name);
    } catch (error) {
      console.error("Error fetching open statuses:", error);
      return [];
    }
  }

  private transformData(data: RawReportType[]): TransformedReportGroup[] {
    const reportGroups = this.getUniqueReportNames(data);
    let records: TransformedReportGroup[] = [];

    reportGroups.forEach((reportGroup) => {
      const filteredRecords = data.filter(
        (record) => record.attributes.report_group_by === reportGroup
      );

      const formatTypes: ReportFormatType[] = filteredRecords.map((record) => ({
        report_id: record.attributes.report_id,
        report_generate: record.attributes.report_generate,
      }));

      records.push({
        report_group_by: filteredRecords[0].attributes.report_group_by,
        formatTypes: formatTypes,
      });
    });

    records = records.sort((a, b) => {
      const nameA = a.report_group_by ? a.report_group_by.toLowerCase() : a.report_group_by;
      const nameB = b.report_group_by ? b.report_group_by.toLowerCase() : b.report_group_by;
      if (nameA < nameB) return -1;
      if (nameA > nameB) return 1;
      return 0;
    });

    return records;
  }

  private getUniqueReportNames(data: RawReportType[]): string[] {
    const uniqueReportNames = new Set<string>();

    data.forEach((item) => {
      if (item.attributes.report_group_by) {
        uniqueReportNames.add(item.attributes.report_group_by);
      }
    });

    return Array.from(uniqueReportNames);
  }

  private createReportMetadata(reportName: string): ReportMetadata {
    return {
      reportName: reportName,
      retryUrl: window.location.href,
      resourceName: reportName,
      resourceType: "Results",
    };
  }

  private generateReport(url: string, options: any): Promise<any> {
    return this.backendApi.publicApiPost(url, options);
  }

  private setupCancellationHandler(socket: WebSocket): void {
    const unsubscribe = this.eventService.subscribe("reportGenerationCancelled", () => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
      this.cleanup();
    });

    if (typeof unsubscribe === "function") {
      this.cancellationUnsubscribe = unsubscribe;
    }
  }

  private async handleWebSocketMessage(
    message: WebSocketMessage,
    orgId: string | number,
    reportId: string | number,
    reportName: string,
    filterExpression: FilterExpression,
    socket: WebSocket
  ): Promise<void> {
    switch (message.action) {
      case "CONNECTION_ID": {
        const connectionId = message.data?.connectionId;
        const url = this.apiPath.reports.generateReportUrl(orgId, reportId, connectionId);

        const body = {
          dataArgs: { ...filterExpression },
          reportMetadata: this.createReportMetadata(reportName),
        };

        const options = {};
        await this.generateReport(url, { options, body });
        break;
      }

      case "REPORT": {
        if (message.data?.reportStatus) {
          this.eventService.publish("reportGenerated", {
            reportDownloadURL: message.data.reportDownloadURL,
            reportStatus: message.data.reportStatus,
          });
        } else {
          this.eventService.publish("reportGenerationFailure", {
            reportStatus: message.data?.reportStatus,
          });
        }

        socket.close();
        this.cleanup();
        break;
      }

      default:
        break;
    }
  }

  private cleanup(): void {
    if (this.activeSocket) {
      if (this.activeSocket.readyState === WebSocket.OPEN) {
        this.activeSocket.close();
      }
      this.activeSocket = null;
    }

    if (this.cancellationUnsubscribe) {
      this.cancellationUnsubscribe();
      this.cancellationUnsubscribe = null;
    }
  }
}

export function createDownloadReportsBackendService(
  dependencies: DownloadReportsBackendDependencies
): DownloadReportsBackendService {
  return new DownloadReportsBackendService(dependencies);
}

